/**
 * Component: Widget configuration fetch
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R14
 *
 * Second call the widget makes during initialisation
 * (after the R18 version handshake): fetch
 * GET /api/widget/config via the authenticated
 * WidgetApiClient and return a typed ``WidgetConfig`` blob.
 *
 * The config payload is the contract between
 * CONFIG-V1-R18 (backend storage) and the widget runtime:
 *
 *   - ``enabled_surfaces`` drives the R15 render-allowlist.
 *   - ``theme`` drives the R16 CSS-custom-property overlay.
 *   - ``allowed_origins`` is admin reference; actual CORS
 *     enforcement happens at the gateway.
 *   - ``deployment_settings`` is the escape-hatch object
 *     for future R## requirements.
 *
 * This commit ships the fetch + shape validation only. The
 * R08 401-retry layer ships in the final Batch B commit
 * and wraps this call (without changing the public API
 * surface).
 */

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';

/**
 * Canonical list of WIDGET-V1 surfaces. Pinned verbatim
 * against the backend's ``WidgetSurface`` Literal so an
 * accidental drift between frontend and backend trips
 * widget initialisation rather than silently dropping
 * unknown surfaces.
 */
export const WIDGET_SURFACES = [
  'chat',
  'galen-search',
  'semantic-search',
  'simple-search',
  'browse',
] as const;

export type WidgetSurface = (typeof WIDGET_SURFACES)[number];

/**
 * Runtime-typed widget configuration. Mirrors the Pydantic
 * ``WidgetConfig`` shipped in CONFIG-V1-R18.
 */
export interface WidgetConfig {
  enabled_surfaces: WidgetSurface[];
  theme: Record<string, string>;
  allowed_origins: string[];
  deployment_settings: Record<string, unknown>;
  /**
   * AB#4401: display name the Chat surface shows for the
   * assistant ("Galen" by default; per-deployment so a
   * customer can white-label). Optional here so a widget
   * bundle deployed against an older backend that doesn't
   * yet emit the field still initialises — the Chat surface
   * falls back to "Galen".
   */
  assistant_name?: string;

  /**
   * AB#4404: optional HTML block the widget renders
   * (sanitised) at the top of the mounted surface — a
   * per-deployment intro / landing blurb. Empty / absent =
   * no blurb. Optional here so an older backend's config
   * blob (no field) still initialises.
   */
  landing_content?: string;
}

/**
 * Response payload shape returned by
 * ``GET /api/widget/config``. Wraps ``WidgetConfig`` with
 * the version + audit metadata the admin UI uses.
 *
 * BROWSE-V1-R18 / R20: ``enable_browse_links`` mirrors the
 * deployment-time backend ``browse_links_enabled`` Setting.
 * The widget retrieval surfaces gate the cross-module
 * BrowseLink on this flag. Optional / default ``false`` so
 * a widget bundle deployed against an older backend that
 * does not yet emit the field still initialises (the link
 * just stays hidden in that mode).
 */
export interface WidgetConfigResponse {
  config: WidgetConfig;
  version: number;
  updated_at: string;
  updated_by: string;
  enable_browse_links: boolean;
  /**
   * GALEN-CHAT-V1-R02: the deployment's branding
   * ``disclaimers.safety_disclaimer`` text, surfaced here so the
   * Chat surface can render the persistent clinical-safety
   * disclaimer below the input. Absent / ``null`` when the
   * deployment hasn't configured one — the Chat surface then
   * falls back to its built-in default. Optional so a widget
   * bundle deployed against an older backend still initialises.
   */
  chat_safety_disclaimer?: string | null;
  /**
   * WIDGET-V1-R26: mirrors the deployment-time backend
   * ``widget_anonymous_access_enabled`` Setting. When true, the
   * deployment offers anonymous (no sign-in) widget access — the
   * widget can mint an ``anonymous``-role session token from
   * ``POST /api/widget/anon-session``. Defaults ``false`` so a
   * bundle deployed against an older backend that does not emit the
   * field still initialises. The server-side mint endpoint is the
   * actual gate (it 404s when off); this flag is informational.
   */
  anonymous_access_enabled: boolean;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isStringDict(value: unknown): value is Record<string, string> {
  return (
    isObject(value) &&
    Object.values(value).every((item) => typeof item === 'string')
  );
}

function isWidgetSurface(value: unknown): value is WidgetSurface {
  return (
    typeof value === 'string' &&
    (WIDGET_SURFACES as readonly string[]).includes(value)
  );
}

/**
 * Run structural validation on the response body so a
 * backend / widget version drift produces a clear error
 * rather than a mysterious runtime failure two layers
 * deeper in the init pipeline.
 *
 * Any field that fails validation raises ``WidgetError``
 * with enough context for the host error callback to
 * render actionable diagnostics.
 */
function assertWidgetConfigResponse(
  candidate: unknown,
): asserts candidate is WidgetConfigResponse {
  if (!isObject(candidate)) {
    throw new WidgetError(
      `GET /api/widget/config did not return a JSON object: ${JSON.stringify(candidate)}`,
    );
  }
  const { config, version, updated_at, updated_by } = candidate;

  if (!isObject(config)) {
    throw new WidgetError(
      '/api/widget/config response is missing the config field',
    );
  }
  if (
    !Array.isArray(config.enabled_surfaces) ||
    !config.enabled_surfaces.every(isWidgetSurface)
  ) {
    throw new WidgetError(
      `/api/widget/config config.enabled_surfaces is not a list of known surfaces: ${JSON.stringify(
        config.enabled_surfaces,
      )}`,
    );
  }
  if (!isStringDict(config.theme)) {
    throw new WidgetError(
      `/api/widget/config config.theme must be an object of string values`,
    );
  }
  if (!isStringArray(config.allowed_origins)) {
    throw new WidgetError(
      `/api/widget/config config.allowed_origins must be an array of strings`,
    );
  }
  if (!isObject(config.deployment_settings)) {
    throw new WidgetError(
      `/api/widget/config config.deployment_settings must be an object`,
    );
  }
  // AB#4401: optional (older backend may not emit it); when
  // present it must be a non-empty string. The Chat surface
  // defaults to "Galen" when absent.
  if (
    config.assistant_name !== undefined &&
    (typeof config.assistant_name !== 'string' ||
      config.assistant_name.trim().length === 0)
  ) {
    throw new WidgetError(
      `/api/widget/config config.assistant_name must be a non-empty string when present`,
    );
  }
  // AB#4404: optional; when present it must be a string (may be
  // empty — empty means "no landing blurb").
  if (
    config.landing_content !== undefined &&
    typeof config.landing_content !== 'string'
  ) {
    throw new WidgetError(
      `/api/widget/config config.landing_content must be a string when present`,
    );
  }
  if (typeof version !== 'number') {
    throw new WidgetError(
      `/api/widget/config response.version must be a number`,
    );
  }
  if (typeof updated_at !== 'string') {
    throw new WidgetError(
      `/api/widget/config response.updated_at must be an ISO-8601 string`,
    );
  }
  if (typeof updated_by !== 'string') {
    throw new WidgetError(
      `/api/widget/config response.updated_by must be a string`,
    );
  }
  // BROWSE-V1-R18 / R20: optional with default false so a
  // widget bundle deployed against an older backend (one
  // that pre-dates the field) still initialises. When
  // present it must be a boolean — anything else is a
  // protocol drift worth surfacing rather than silently
  // coercing.
  const enableBrowseLinks = (candidate as Record<string, unknown>)[
    'enable_browse_links'
  ];
  if (enableBrowseLinks === undefined) {
    (candidate as Record<string, unknown>)['enable_browse_links'] = false;
  } else if (typeof enableBrowseLinks !== 'boolean') {
    throw new WidgetError(
      `/api/widget/config response.enable_browse_links must be a boolean when present`,
    );
  }
  // GALEN-CHAT-V1-R02: optional; when present must be a string (or
  // null — the backend sends null when no branding disclaimer is
  // configured). Anything else is a protocol drift worth surfacing.
  const chatSafetyDisclaimer = (candidate as Record<string, unknown>)[
    'chat_safety_disclaimer'
  ];
  if (
    chatSafetyDisclaimer !== undefined &&
    chatSafetyDisclaimer !== null &&
    typeof chatSafetyDisclaimer !== 'string'
  ) {
    throw new WidgetError(
      `/api/widget/config response.chat_safety_disclaimer must be a string when present`,
    );
  }
  // WIDGET-V1-R26: optional with default false (older-backend
  // tolerance). When present it must be a boolean.
  const anonymousAccessEnabled = (candidate as Record<string, unknown>)[
    'anonymous_access_enabled'
  ];
  if (anonymousAccessEnabled === undefined) {
    (candidate as Record<string, unknown>)['anonymous_access_enabled'] = false;
  } else if (typeof anonymousAccessEnabled !== 'boolean') {
    throw new WidgetError(
      `/api/widget/config response.anonymous_access_enabled must be a boolean when present`,
    );
  }
}

/**
 * Fetch the active widget configuration for the
 * authenticated tenant.
 *
 * Transport failures bubble as ``WidgetNetworkError`` from
 * the client; HTTP failures bubble as ``WidgetApiError``;
 * structural failures in a 200-response body bubble as
 * ``WidgetError`` so the init pipeline can surface each in
 * the host ``onError`` callback with a stable shape.
 */
export async function fetchWidgetConfig(
  client: WidgetApiClient,
): Promise<WidgetConfigResponse> {
  const payload = await client.getJson<unknown>('/api/widget/config');
  assertWidgetConfigResponse(payload);
  return payload;
}
